/*!
 * PatternFly Elements: PfeCodeblock 1.12.3
 * @license
 * Copyright 2021 Red Hat, Inc.
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 * 
*/

// Import polyfills: Array.includes
import "./polyfills--pfe-codeblock.js";

import PFElement from "../../pfelement/dist/pfelement.js";
import Prism from "prismjs";

const observerConfig = {
  childList: true,
  subtree: true,
  characterData: true,
};

class PfeCodeblock extends PFElement {

  // Injected at build-time
  static get version() {
    return "1.12.3";
  }

  // Injected at build-time
  get html() {
    return `
<style>:host code[class*=language-],:host pre[class*=language-]{text-align:left;white-space:pre;word-spacing:normal;word-break:normal;word-wrap:normal;color:#6a6e73;background:#f5f5f5;font-family:"Overpass Mono",Consolas,Monaco,"Andale Mono",monospace;font-family:var(--pfe-theme--font-family--code, "Overpass Mono", Consolas, Monaco, "Andale Mono", monospace);font-size:1em;line-height:1.5em;-moz-tab-size:4;-o-tab-size:4;tab-size:4;-webkit-hyphens:none;-ms-hyphens:none;hyphens:none}:host code[class*=language-] ::-moz-selection,:host code[class*=language-]::-moz-selection,:host pre[class*=language-] ::-moz-selection,:host pre[class*=language-]::-moz-selection{background:#cceae7;color:#263238}:host code[class*=language-] ::selection,:host code[class*=language-]::selection,:host pre[class*=language-] ::selection,:host pre[class*=language-]::selection{background:#cceae7;color:#263238}:host :not(pre)>code[class*=language-]{white-space:normal;border-radius:.2em;padding:.1em}:host pre[class*=language-]{overflow:auto;position:relative;margin:.5em 0;padding:1.25em 1em}:host .language-css>code,:host .language-sass>code,:host .language-scss>code{color:#b300b3}:host [class*=language-] .namespace{opacity:.7}:host .token.atrule{color:#40199a}:host .token.attr-name{color:#06c}:host .token.attr-value{color:#b300b3}:host .token.attribute{color:#b300b3}:host .token.boolean{color:#40199a}:host .token.builtin{color:#06c}:host .token.cdata{color:#06c}:host .token.char{color:#06c}:host .token.class{color:#06c}:host .token.class-name{color:#06c}:host .token.comment{color:#6a6e73}:host .token.constant{color:#40199a}:host .token.deleted{color:#c9190b}:host .token.doctype{color:#6a6e73}:host .token.entity{color:#c9190b}:host .token.function{color:#40199a}:host .token.hexcode{color:#b300b3}:host .token.id{color:#40199a;font-weight:700}:host .token.important{color:#40199a;font-weight:700}:host .token.inserted{color:#06c}:host .token.keyword{color:#40199a}:host .token.number{color:#b300b3}:host .token.operator{color:#06c}:host .token.prolog{color:#6a6e73}:host .token.property{color:#06c}:host .token.pseudo-class{color:#b300b3}:host .token.pseudo-element{color:#b300b3}:host .token.punctuation{color:#06c}:host .token.regex{color:#06c}:host .token.selector{color:#c9190b}:host .token.string{color:#b300b3}:host .token.symbol{color:#40199a}:host .token.tag{color:#c9190b}:host .token.unit{color:#b300b3}:host .token.url{color:#c9190b}:host .token.variable{color:#c9190b}:host([code-theme=dark]) code[class*=language-],:host([code-theme=dark]) pre[class*=language-]{text-align:left;white-space:pre;word-spacing:normal;word-break:normal;word-wrap:normal;color:#d2d2d2;background:#3c3f42;font-family:"Overpass Mono",Consolas,Monaco,"Andale Mono",monospace;font-family:var(--pfe-theme--font-family--code, "Overpass Mono", Consolas, Monaco, "Andale Mono", monospace);font-size:1em;line-height:1.5em;-moz-tab-size:4;-o-tab-size:4;tab-size:4;-webkit-hyphens:none;-ms-hyphens:none;hyphens:none}:host([code-theme=dark]) code[class*=language-] ::-moz-selection,:host([code-theme=dark]) code[class*=language-]::-moz-selection,:host([code-theme=dark]) pre[class*=language-] ::-moz-selection,:host([code-theme=dark]) pre[class*=language-]::-moz-selection{background:#363636}:host([code-theme=dark]) code[class*=language-] ::selection,:host([code-theme=dark]) code[class*=language-]::selection,:host([code-theme=dark]) pre[class*=language-] ::selection,:host([code-theme=dark]) pre[class*=language-]::selection{background:#363636}:host([code-theme=dark]) :not(pre)>code[class*=language-]{white-space:normal;border-radius:.2em;padding:.1em}:host([code-theme=dark]) pre[class*=language-]{overflow:auto;position:relative;margin:.5em 0;padding:1.25em 1em}:host([code-theme=dark]) pre[class*=language-].line-numbers{position:relative;padding-left:3.8em;counter-reset:linenumber}:host([code-theme=dark]) .language-css>code,:host([code-theme=dark]) .language-sass>code,:host([code-theme=dark]) .language-scss>code{color:#ace12e}:host([code-theme=dark]) [class*=language-] .namespace{opacity:.7}:host([code-theme=dark]) .token.atrule{color:#b2a3ff}:host([code-theme=dark]) .token.attr-name{color:#ace12e}:host([code-theme=dark]) .token.attr-value{color:#ace12e}:host([code-theme=dark]) .token.attribute{color:#ace12e}:host([code-theme=dark]) .token.boolean{color:#b2a3ff}:host([code-theme=dark]) .token.builtin{color:#ace12e}:host([code-theme=dark]) .token.cdata{color:#73bcf7}:host([code-theme=dark]) .token.char{color:#73bcf7}:host([code-theme=dark]) .token.class{color:#ace12e}:host([code-theme=dark]) .token.class-name{color:#73bcf7}:host([code-theme=dark]) .token.comment{color:#d2d2d2}:host([code-theme=dark]) .token.constant{color:#b2a3ff}:host([code-theme=dark]) .token.deleted{color:#ff8a80}:host([code-theme=dark]) .token.doctype{color:#d2d2d2}:host([code-theme=dark]) .token.entity{color:#ff8a80}:host([code-theme=dark]) .token.function{color:#b2a3ff}:host([code-theme=dark]) .token.hexcode{color:#73bcf7}:host([code-theme=dark]) .token.id{color:#b2a3ff;font-weight:700}:host([code-theme=dark]) .token.important{color:#b2a3ff;font-weight:700}:host([code-theme=dark]) .token.inserted{color:#73bcf7}:host([code-theme=dark]) .token.keyword{color:#b2a3ff}:host([code-theme=dark]) .token.number{color:#ace12e}:host([code-theme=dark]) .token.operator{color:#73bcf7}:host([code-theme=dark]) .token.prolog{color:#d2d2d2}:host([code-theme=dark]) .token.property{color:#73bcf7}:host([code-theme=dark]) .token.pseudo-class{color:#ace12e}:host([code-theme=dark]) .token.pseudo-element{color:#ace12e}:host([code-theme=dark]) .token.punctuation{color:#73bcf7}:host([code-theme=dark]) .token.regex{color:#73bcf7}:host([code-theme=dark]) .token.selector{color:#ff8a80}:host([code-theme=dark]) .token.string{color:#ace12e}:host([code-theme=dark]) .token.symbol{color:#b2a3ff}:host([code-theme=dark]) .token.tag{color:#ff8a80}:host([code-theme=dark]) .token.unit{color:#ace12e}:host([code-theme=dark]) .token.url{color:#ff8a80}:host([code-theme=dark]) .token.variable{color:#ff8a80}pre[class*=language-].line-numbers{position:relative;padding-left:3.8em;counter-reset:linenumber}pre[class*=language-].line-numbers>code{position:relative;white-space:inherit}.line-numbers .line-numbers-rows{position:absolute;pointer-events:none;top:0;font-size:100%;left:-3.8em;width:3em;letter-spacing:-1px;border-right:1px solid #999;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.line-numbers-rows>span{display:block;counter-increment:linenumber}.line-numbers-rows>span:before{content:counter(linenumber);color:#999;display:block;padding-right:.8em;text-align:right}:host{display:block}:host([hidden]){display:none} /*# sourceMappingURL=pfe-codeblock.min.css.map */</style>
<slot></slot>`;
  }

  static get tag() {
    return "pfe-codeblock";
  }

  static get meta() {
    return {
      title: "Codeblock",
      description: "Render code in a styled and formatted way",
    };
  }

  get templateUrl() {
    return "pfe-codeblock.html";
  }

  get styleUrl() {
    return "pfe-codeblock.scss";
  }

  static get events() {
    return {};
  }

  // Declare the type of this component
  static get PfeType() {
    return PFElement.PfeTypes.Content;
  }

  static get properties() {
    return {
      codeLanguage: {
        title: "Code Language",
        type: String,
        values: ["markup", "html", "xml", "svg", "mathml", "css", "clike", "javascript", "js"],
        default: "markup",
        observer: "_attributeChanged",
      },
      codeLineNumbers: {
        title: "Enable Line Numbers",
        type: Boolean,
        default: false,
        observer: "_attributeChanged",
      },
      codeLineNumberStart: {
        title: "Set Line Number Start Value",
        type: Number,
        default: 1,
        observer: "_attributeChanged",
      },
      codeTheme: {
        title: "Code Theme",
        type: String,
        values: ["dark", "light"],
        default: "light",
        observer: "_attributeChanged",
      },
    };
  }

  static get slots() {
    return {};
  }

  get codeblock() {
    return this._codeblock;
  }

  set codeblock(text) {
    if (!text) {
      return;
    }

    this._codeblock = text;
    this.renderCodeblock();
  }

  // Declare the type of this component
  static get PfeType() {
    return PFElement.PfeTypes.Content;
  }

  constructor() {
    super(PfeCodeblock, { type: PfeCodeblock.PfeType });

    this._codeblock = null;
    this._codeblockRender = null;
    this._codeblockRenderOuterPreTag = null;
    this._codeblockContainer = null;
    this._readyStateChangeHandler = this._readyStateChangeHandler.bind(this);

    // Add mutation observer to track text changes in the dom
    this._observer = new MutationObserver((mutationList, observer) => {
      if (!this._codeblockContainer.textContent) {
        this._codeblockRender.innerHTML = "";
        return;
      }

      // TODO: when we stop supporting IE11, the need to disconnect and
      // then reconnect will no longer be needed
      if (window.ShadyCSS) {
        observer.disconnect();
      }

      this.codeblock = this._codeblockContainer.textContent;

      if (window.ShadyCSS) {
        this._muationObserve();
      }
    });
  }

  //return class for line numbers
  lineNumberCssClass() {
    return this.codeLineNumbers ? " line-numbers" : "";
  }

  //return a valid prism.js language css class
  codePrismLanguage() {
    return "language-" + this.codeLanguage;
  }

  //return a prism.js language lib
  codePrismLanguageLoad() {
    return Prism.languages[this.codeLanguage];
  }

  //return applied classes for pre
  appliedCssClasss() {
    return this.codePrismLanguage() + this.lineNumberCssClass();
  }

  //apply classes to the markup for prism.js
  setComponentClasses() {
    this._codeblockRender.setAttribute("class", this.codePrismLanguage());
    this._codeblockRenderOuterPreTag.setAttribute("class", this.appliedCssClasss());
    if (this.codeLineNumberStart !== 1) {
      this._codeblockRenderOuterPreTag.style.counterReset = "linenumber " + (this.codeLineNumberStart - 1);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    //create dom elements and attach language styles
    this._codeblockRenderOuterPreTag = document.createElement("pre");
    this._codeblockRender = document.createElement("code");
    this._codeblockRender.setAttribute("codeblock-render", "");
    this.setComponentClasses();
    this._codeblockRenderOuterPreTag.appendChild(this._codeblockRender);

    //Add to shadow-root
    this.shadowRoot.appendChild(this._codeblockRenderOuterPreTag);

    //Hide dom element and init prism.js
    if (!this._codeblockContainer) {
      this._codeblockContainer = this.querySelector("[codeblock-container]");
      this._codeblockContainer.style.display = "none";
      this._init();
    }
  }

  disconnectedCallback() {
    this._observer.disconnect();
  }

  // Process the attribute change
  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
  }

  _readyStateChangeHandler(event) {
    if (event.target.readyState === "complete") {
      document.removeEventListener("readystatechange", this._readyStateChangeHandler);
      this._init();
    }
  }

  _attributeChanged() {
    if (this._codeblockRender !== null && this._codeblockRenderOuterPreTag !== null) {
      this.updateCodeblock();
    }
  }

  _init() {
    if (this._codeblockContainer.textContent) {
      let tmpCodeblockObject = this.trimWhitespaceLines(this._codeblockContainer.textContent);
      this.codeblock = tmpCodeblockObject.stringValue;
    }
    this._muationObserve();
  }

  //Accepts string and Returns trimed string and new line count
  trimWhitespaceLines(stringToTrim) {
    //return if nothing passed
    if (!stringToTrim) {
      return "";
    }
    let returnValue = { stringValue: "", lineCount: 0 };

    let tmpTrimArray = stringToTrim.trim().split("\n");

    let tmpLineCount = tmpTrimArray.length;
    returnValue.stringValue = tmpTrimArray.join("\n");
    returnValue.lineCount = tmpLineCount;

    return returnValue;
  }

  processLineNumbers(htmlStringToProcess) {
    //return if nothing passed
    if (!htmlStringToProcess) {
      return "";
    }

    let returnHtmlString = htmlStringToProcess + '<span class="line-numbers-rows" aria-hidden="true">';
    let lineStringObject = this.trimWhitespaceLines(htmlStringToProcess);
    for (var i = 0, len = lineStringObject.lineCount; i < len; i++) {
      returnHtmlString = returnHtmlString + "<span></span>";
    }
    returnHtmlString = returnHtmlString + "</span>";
    return returnHtmlString;
  }

  updateCodeblock() {
    this.setComponentClasses();
    this.renderCodeblock();
  }

  renderCodeblock() {
    this._codeblockRender.innerHTML = Prism.highlight(
      this._codeblock,
      this.codePrismLanguageLoad(),
      this.codePrismLanguage()
    );

    if (this.codeLineNumbers) {
      let htmlString = this.processLineNumbers(this._codeblockRender.innerHTML);
      this._codeblockRender.innerHTML = htmlString;
    }
  }

  _muationObserve() {
    this._observer.observe(this._codeblockContainer, observerConfig);
  }
}

PFElement.create(PfeCodeblock);

export default PfeCodeblock;
